import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useMemo, useState, useEffect, useRef } from "react";
import { Box, IconButton, Typography, Dialog, DialogContent } from "@mui/material";
import CommonDrawerLayout from "../../../common_components/CommonDrawerLayout";
import { Dropdown } from "../../../common_components/FormInputs";
import { toast } from "../../../common_components/Toast/ToastUtils";
import CommonLoader from "../../../common_components/CommonLoader";
import AppButton from "../../../common_components/AppButton";
import HoverTooltip from "../../../common_components/hoverOverlay/HoverTooltip";
const AdPreview = ({ open, onClose, previewRow, previewFormat, previewFormatOptions, onPreviewFormatChange, loading, error, html, previewUrl, }) => {
    const [shareOpen, setShareOpen] = useState(false);
    const adNameRef = useRef(null);
    const [isTruncated, setIsTruncated] = useState(false);
    const [contentReady, setContentReady] = useState(false);
    const htmlContainerRef = useRef(null);
    const previewFrameSx = {
        width: "100%",
        height: "630px",
        borderRadius: "16px",
        overflow: "hidden",
        display: "flex",
        alignItems: "center",
        justifyContent: "center"
    };
    const shareLink = useMemo(() => (previewUrl || "").trim(), [previewUrl]);
    const handleCopyShareLink = async () => {
        if (!shareLink) {
            toast.error("Share link is not available for this preview.");
            return;
        }
        try {
            if (navigator?.clipboard?.writeText) {
                await navigator.clipboard.writeText(shareLink);
            }
            else {
                const textarea = document.createElement("textarea");
                textarea.value = shareLink;
                textarea.style.position = "fixed";
                textarea.style.left = "-9999px";
                textarea.style.top = "-9999px";
                document.body.appendChild(textarea);
                textarea.focus();
                textarea.select();
                document.execCommand("copy");
                document.body.removeChild(textarea);
            }
            toast.success("Share link copied to clipboard.");
        }
        catch (e) {
            toast.error("Failed to copy share link.");
        }
    };
    useEffect(() => {
        const el = adNameRef.current;
        if (el) {
            setIsTruncated(el.scrollWidth > el.offsetWidth);
        }
    }, [previewRow?.ad_name, open]);
    useEffect(() => {
        setContentReady(false);
    }, [html, previewUrl]);
    // Track every media element the returned HTML actually renders — img tags,
    // iframes, and css background-image (Meta's collage-style previews use divs
    // with background-image, not <img> tags). contentReady flips exactly once,
    // when the tracked-pending count reaches zero. No timers/heuristics race
    // against that single source of truth.
    useEffect(() => {
        if (!html) {
            return;
        }
        const runId = Math.random().toString(36).slice(2, 8);
        const container = htmlContainerRef.current;
        if (!container) {
            console.warn(`[AdPreview][${runId}] detect-effect: container ref missing, forcing contentReady=true`);
            setContentReady(true);
            return;
        }
        let settled = false;
        let pending = 0;
        const trackedBgUrls = new Set();
        const cleanups = [];
        const finish = (reason) => {
            if (settled) {
                return;
            }
            settled = true;
            setContentReady(true);
        };
        const track = (target, label) => {
            pending += 1;
            const onDone = (evt) => {
                pending = Math.max(0, pending - 1);
                if (pending === 0)
                    finish(`all-media-settled:${label}`);
            };
            target.addEventListener("load", onDone, { once: true });
            target.addEventListener("error", onDone, { once: true });
            cleanups.push(() => {
                target.removeEventListener("load", onDone);
                target.removeEventListener("error", onDone);
            });
        };
        const extractBgUrl = (el) => {
            const bg = window.getComputedStyle(el).backgroundImage;
            if (!bg || bg === "none")
                return null;
            const match = /url\(["']?([^"')]+)["']?\)/.exec(bg);
            return match ? match[1] : null;
        };
        const visit = (el) => {
            const tag = el.tagName;
            if (tag === "IMG") {
                const img = el;
                if (img.complete && img.naturalWidth > 0) {
                    return;
                }
                track(img, `img:${img.src}`);
                return;
            }
            if (tag === "IFRAME") {
                const iframe = el;
                track(iframe, `iframe:${iframe.src}`);
                return;
            }
            const bgUrl = extractBgUrl(el);
            if (bgUrl && !trackedBgUrls.has(bgUrl)) {
                trackedBgUrls.add(bgUrl);
                const probe = new Image();
                track(probe, `bg:${bgUrl}`);
                probe.src = bgUrl;
            }
        };
        const scan = (root) => {
            visit(root);
            root.querySelectorAll("*").forEach(visit);
        };
        scan(container);
        if (pending === 0)
            finish("nothing-to-wait-for");
        const observer = new MutationObserver((mutations) => {
            mutations.forEach((m) => {
                m.addedNodes.forEach((node) => {
                    if (node.nodeType === 1) {
                        scan(node);
                    }
                });
            });
        });
        observer.observe(container, { childList: true, subtree: true });
        // Safety net: never let a stuck embed keep the loader up forever.
        const hardTimeoutId = window.setTimeout(() => {
            console.warn(`[AdPreview][${runId}] hard timeout (15s) reached, pending was ${pending}`);
            finish("hard-timeout");
        }, 15000);
        return () => {
            observer.disconnect();
            window.clearTimeout(hardTimeoutId);
            cleanups.forEach((remove) => remove());
        };
    }, [html]);
    const showLoader = loading || ((!!html || !!previewUrl) && !contentReady);
    // useEffect(() => {
    //   console.log("[AdPreview] showLoader recomputed", {
    //     showLoader,
    //     loading,
    //     hasHtml: !!html,
    //     hasPreviewUrl: !!previewUrl,
    //     contentReady,
    //   });
    // }, [showLoader, loading, html, previewUrl, contentReady]);
    const PreviewContent = useMemo(() => {
        if (error) {
            return _jsx(Typography, { sx: { fontSize: 14, color: "#B91C1C" }, children: error });
        }
        if (!html && !previewUrl) {
            if (loading) {
                return (_jsx(Box, { sx: { width: 300, height: 450, display: "flex", alignItems: "center", justifyContent: "center" }, children: _jsx(CommonLoader, { className: "table-loader" }) }));
            }
            return _jsx("img", { src: "/assets/image-placeholder.svg" });
        }
        return (_jsxs(Box, { sx: { ...previewFrameSx, position: "relative" }, children: [showLoader && (_jsx(Box, { sx: {
                        position: "absolute",
                        inset: 0,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        backgroundColor: "#fff",
                        zIndex: 1,
                    }, children: _jsx(CommonLoader, { className: "table-loader" }) })), html ? (_jsx(Box, { ref: htmlContainerRef, sx: {
                        ...previewFrameSx,
                        visibility: showLoader ? "hidden" : "visible",
                    }, dangerouslySetInnerHTML: { __html: html } })) : (_jsx("iframe", { src: previewUrl, loading: "lazy", onLoad: () => {
                        setContentReady(true);
                    }, style: {
                        width: "500px",
                        height: "500px",
                        border: "none",
                        borderRadius: "16px",
                        visibility: showLoader ? "hidden" : "visible",
                    } }, previewUrl))] }));
    }, [loading, error, html, previewUrl, showLoader]);
    return (_jsx(CommonDrawerLayout, { open: open, onClose: onClose, title: "Ad Preview", headerActions: _jsx(IconButton, { size: "small", onClick: () => setShareOpen(true), "aria-label": "Share this ad", children: _jsx("img", { src: "/assets/share.svg", alt: "share" }) }), children: _jsxs(Box, { sx: { p: 1, display: "flex", flexDirection: "column", gap: 1 }, children: [_jsx(Dialog, { open: shareOpen, onClose: () => setShareOpen(false), maxWidth: "md", fullWidth: true, keepMounted: true, children: _jsxs(DialogContent, { sx: { p: 1 }, children: [_jsxs(Box, { sx: { display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }, children: [_jsx(Typography, { sx: { fontSize: 16, fontWeight: 600, color: "#111827" }, children: "Share this ad" }), _jsx(IconButton, { size: "small", onClick: () => setShareOpen(false), "aria-label": "Close share dialog", children: _jsx("span", { style: { fontSize: 18, lineHeight: 1 }, children: "\u00D7" }) })] }), _jsx(Typography, { sx: { fontSize: 14, fontWeight: 600, color: "#374151", mb: 1 }, children: "Copy this URL to invite people you work with to see this ad." }), _jsx(Typography, { sx: { fontSize: 13, color: "#6B7280", mb: 1 }, children: "When people visit the URL, they'll see a request to let us show this ad in all selected placements for 24 hours." }), _jsxs(Box, { sx: {
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 1,
                                    border: "1px solid #E5E7EB",
                                    borderRadius: "8px",
                                    p: 1,
                                    backgroundColor: "#fff",
                                    mb: 1,
                                }, children: [_jsx(Typography, { sx: {
                                            flex: 1,
                                            fontSize: 14,
                                            color: "#FF008E",
                                            overflow: "hidden",
                                            textOverflow: "ellipsis",
                                            whiteSpace: "nowrap",
                                        }, title: shareLink || "", children: shareLink || "Share link is not available" }), _jsx(AppButton, { variant: "contained", className: "app-common-button", onClick: handleCopyShareLink, disabled: !shareLink, sx: {
                                            backgroundColor: "#111827",
                                            borderColor: "#111827",
                                            "&:hover": { backgroundColor: "#000", borderColor: "#000" },
                                            minWidth: 80,
                                        }, children: "Copy" })] }), _jsx(Typography, { sx: { fontSize: 13, color: "#6B7280", mb: 3 }, children: "People who see this ad with your URL will not be included as an ad view, so you will not be charged for their impressions." })] }) }), _jsxs(Box, { sx: {
                        backgroundColor: "white",
                        padding: "8px",
                        borderRadius: "8px",
                        border: "1px solid #E5E7EB",
                    }, children: [_jsx(HoverTooltip, { title: previewRow?.ad_name || "", disableHoverListener: !isTruncated, placement: "top", arrow: true, children: _jsx(Box, { ref: adNameRef, sx: {
                                    fontSize: 14,
                                    fontWeight: 600,
                                    color: "#FF008E",
                                    whiteSpace: "nowrap",
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    pb: 1,
                                }, children: previewRow?.ad_name || "--" }) }), _jsx(Box, { sx: {
                                display: "flex",
                                flexDirection: "column",
                                gap: 0.5,
                            }, children: _jsx(Box, { sx: { width: "100%" }, children: _jsx(Dropdown, { value: previewFormat, options: previewFormatOptions, onChange: (val) => {
                                        const next = Array.isArray(val) ? String(val[0]) : String(val);
                                        onPreviewFormatChange(next);
                                    }, placeholder: "Select format", disabled: !Array.isArray(previewFormatOptions) || previewFormatOptions.length === 0 }) }) })] }), _jsx(Box, { sx: {
                        width: "100%",
                        flex: 1,
                        maxHeight: "100%",
                        borderRadius: "16px",
                        backgroundColor: "#fff",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        padding: "8px",
                    }, children: PreviewContent })] }) }));
};
export default AdPreview;
